import type { TileMapRenderer, TileSelectedEvent } from '../renderer/tilemap/TileMapRenderer';
import type { TilePosition } from '../model/gamestate/GameState';
import type { Input, InputType } from '../model/Input';
import type { EventHub } from '../events/EventHub';
import { grid as defaultGrid, type GridGeometry } from '../grid';
import { calculateTargetAngleFromTileCenters } from '../utils/rotation';
import type { AdminBiomeBrush, AdminTerrainBrush } from '../ui/admin';
import type { PathTileAtlasId } from '../terrain/pathTileRegistry';
import {
  MAP_OBJECT_PLACEMENT_SCALE_CHANGED_EVENT,
  getVisualAssetSizeMultiplier,
} from '../visualAssets/debugPlacementOffset';
import { resolveTileDrawMetrics } from '../renderer/tilemap/board';

const MAP_OBJECT_DRAG_STARTED_EVENT = 'map-object-drag-started';
const MAP_OBJECT_DRAG_ENDED_EVENT = 'map-object-drag-ended';
// Offsets are persisted as normalized tile-space values in [-1, 1].
const MAP_OBJECT_OFFSET_NUDGE_STEP = 1 / 32;

export type InputIntentDetail =
  | { type: 'moveToTile'; position: TilePosition }
  | { type: 'turn'; direction: number }
  | { type: 'interact' }
  | { type: 'useSkill'; skillId: string; inputTypes?: InputType[]; inputs?: Input[] }
  | {
    type: 'useItem';
    itemId: string;
    inputTypes?: InputType[];
    inputs?: Input[];
  }
  | { type: 'useItemOnTile'; itemId: string; inventoryId: string; targetPosition: TilePosition };
export const INPUT_INTENT_EVENT = 'input:intent';
export const INPUT_CLEAR_EVENT = 'input:clearQueue';

export interface NpcAttemptEvent {
  action: 'place' | 'remove';
  npcType?: string;
  x: number;
  y: number;
}

export interface MapObjectAttemptEvent {
  action: 'place' | 'remove';
  assetId?: string;
  mapX: number;
  mapY: number;
  placementMode?: 'cursor' | 'tile-anchor';
  tileX?: number;
  tileY?: number;
  offsetX?: number;
  offsetY?: number;
  sizeMultiplier?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  rotationSteps?: number;
  screenX?: number;
  screenY?: number;
}

export type PlacementConfig =
  | { kind: 'npc-place'; npcType: string }
  | { kind: 'npc-remove' }
  | { kind: 'tile-terrain'; terrain: AdminTerrainBrush }
  | { kind: 'tile-terrain-erase' }
  | { kind: 'tile-biome'; biome: AdminBiomeBrush }
  | { kind: 'tile-biome-erase' }
  | { kind: 'tile-biome-region'; biome: AdminBiomeBrush }
  | { kind: 'path-tile-autotile'; atlasId: PathTileAtlasId }
  | { kind: 'path-tile-place'; atlasId: PathTileAtlasId; tileIndex: number }
  | { kind: 'path-tile-erase'; atlasId: PathTileAtlasId }
  | { kind: 'map-object-place'; assetId: string }
  | { kind: 'map-object-remove' }
  | { kind: 'portal-place'; destMapId: string; destX: number; destY: number; autoPortal?: boolean }
  | { kind: 'portal-remove' };

export interface MapTileAttemptEvent {
  layer: 'terrain' | 'biome';
  value: string;
  x: number;
  y: number;
}

export interface MapBiomeRegionAttemptEvent {
  biome: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export interface PathTileAttemptEvent {
  atlasId: PathTileAtlasId;
  tileIndex: number | null;
  autotile?: boolean;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  rotationSteps?: number;
  x: number;
  y: number;
}

export interface PortalAttemptEvent {
  action: 'place' | 'remove';
  x: number;
  y: number;
  destMapId?: string;
  destX?: number;
  destY?: number;
  autoPortal?: boolean;
}

interface InputControllerOptions {
  renderer: TileMapRenderer;
  getReferencePosition: () => TilePosition;
  getReferenceRotation: () => number;
  eventHub: EventHub;
  directionalButtons?: NodeListOf<HTMLButtonElement> | HTMLButtonElement[];
}

const buttonDirectionMap: Record<string, TilePosition> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 }
};

interface MapObjectPlacementTransform {
  offsetX: number;
  offsetY: number;
  sizeMultiplier: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotationSteps: number;
}

type MapObjectPlacementPreviewDetail =
  | ({
    active: true;
    assetId: string;
    previewMode: 'tile-anchor' | 'map-anchor';
    tileX?: number;
    tileY?: number;
    mapX?: number;
    mapY?: number;
    offsetX?: number;
    offsetY?: number;
  } & MapObjectPlacementTransform)
  | ({
    active: false;
    assetId: null;
    previewMode: 'tile-anchor';
  } & MapObjectPlacementTransform);

type PathTilePlacementPreviewDetail =
  | ({
    active: true;
    atlasId: PathTileAtlasId;
    tileIndex: number;
    autotile: boolean;
  } & MapObjectPlacementTransform)
  | { active: false; atlasId: null; tileIndex: null; autotile: false };

type BiomeRegionPlacementPreviewDetail =
  | {
    active: true;
    biome: AdminBiomeBrush;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  }
  | {
    active: false;
    biome: null;
  };

export class InputController {
  private readonly renderer: TileMapRenderer;
  private readonly getReferencePosition: () => TilePosition;
  private readonly getReferenceRotation: () => number;
  private readonly directionalButtons: (HTMLButtonElement | undefined)[];
  private readonly view: HTMLElement | null;
  private readonly events: EventHub;

  private grid: GridGeometry = defaultGrid;
  private interactionMode: 'movement' | 'placement' = 'movement';
  private placementConfig: PlacementConfig | null = null;
  private lastPlacementKey: string | null = null;
  private biomeRegionDrag: { biome: AdminBiomeBrush; start: TilePosition; current: TilePosition } | null = null;
  private draggedMapObjectAssetId: string | null = null;
  private mapObjectPlacementTransform: MapObjectPlacementTransform = {
    offsetX: 0,
    offsetY: 0,
    sizeMultiplier: getVisualAssetSizeMultiplier(),
    flipHorizontal: false,
    flipVertical: false,
    rotationSteps: 0,
  };

  constructor(options: InputControllerOptions) {
    this.renderer = options.renderer;
    this.getReferencePosition = options.getReferencePosition;
    this.getReferenceRotation = options.getReferenceRotation;
    this.directionalButtons = Array.from(options.directionalButtons ?? []);
    this.view = this.renderer.view;
    this.events = options.eventHub;

    this.renderer.addEventListener('tileSelected', this.handleTileSelected as EventListener);
    this.view?.addEventListener('contextmenu', this.handleContextMenu);
    this.view?.addEventListener('pointerup', this.resetPlacementDragState);
    this.view?.addEventListener('pointerleave', this.resetPlacementDragState);
    this.view?.addEventListener('dragover', this.handleDragOver);
    this.view?.addEventListener('dragleave', this.handleDragLeave);
    this.view?.addEventListener('drop', this.handleDrop);
    window.addEventListener('dragend', this.handleWindowDragEnd, true);
    window.addEventListener(MAP_OBJECT_DRAG_STARTED_EVENT, this.handleMapObjectDragStarted as EventListener);
    window.addEventListener(MAP_OBJECT_DRAG_ENDED_EVENT, this.handleMapObjectDragEnded as EventListener);
    this.directionalButtons.forEach((button) => {
      button?.addEventListener('click', this.handleDirectionClick);
    });
    window.addEventListener(MAP_OBJECT_PLACEMENT_SCALE_CHANGED_EVENT, this.handleMapObjectPlacementScaleChanged as EventListener);
    this.emitMapObjectPlacementPreviewChanged();
  }

  public dispose(): void {
    this.renderer.removeEventListener('tileSelected', this.handleTileSelected as EventListener);
    this.view?.removeEventListener('contextmenu', this.handleContextMenu);
    this.view?.removeEventListener('pointerup', this.resetPlacementDragState);
    this.view?.removeEventListener('pointerleave', this.resetPlacementDragState);
    this.view?.removeEventListener('dragover', this.handleDragOver);
    this.view?.removeEventListener('dragleave', this.handleDragLeave);
    this.view?.removeEventListener('drop', this.handleDrop);
    window.removeEventListener('dragend', this.handleWindowDragEnd, true);
    window.removeEventListener(MAP_OBJECT_DRAG_STARTED_EVENT, this.handleMapObjectDragStarted as EventListener);
    window.removeEventListener(MAP_OBJECT_DRAG_ENDED_EVENT, this.handleMapObjectDragEnded as EventListener);
    this.directionalButtons.forEach((button) => {
      button?.removeEventListener('click', this.handleDirectionClick);
    });
    window.removeEventListener(MAP_OBJECT_PLACEMENT_SCALE_CHANGED_EVENT, this.handleMapObjectPlacementScaleChanged as EventListener);
  }

  private handleContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  private handleDragOver = (event: DragEvent): void => {
    const assetId = event.dataTransfer?.getData('application/x-mmo-visual-asset')?.trim()
      || this.draggedMapObjectAssetId
      || '';
    if (!assetId) {
      return;
    }
    event.preventDefault();
    this.draggedMapObjectAssetId = assetId;
    event.dataTransfer.dropEffect = 'copy';
    const canvas = this.view;
    if (!(canvas instanceof HTMLCanvasElement)) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;
    const mapPosition = this.renderer.screenToMapPosition(event);
    const tile = this.renderer.screenToTile(screenX, screenY);
    if (!mapPosition || !tile) {
      return;
    }
    const tileMetrics = resolveTileDrawMetrics(this.grid, 1, tile.x, tile.y);
    const transform = this.getMapObjectPlacementTransform();
    this.emitMapObjectPlacementPreviewChanged({
      active: true,
      assetId,
      previewMode: 'map-anchor',
      tileX: tile.x,
      tileY: tile.y,
      mapX: mapPosition.x,
      mapY: mapPosition.y,
      offsetX: ((mapPosition.x - tileMetrics.centerX) / tileMetrics.spanWidth) + transform.offsetX,
      offsetY: ((mapPosition.y - tileMetrics.centerY) / tileMetrics.spanHeight) + transform.offsetY,
      ...transform,
    });
    this.renderer.requestRender();
  };

  private handleDragLeave = (): void => {
    if (!this.draggedMapObjectAssetId) {
      return;
    }
    this.draggedMapObjectAssetId = null;
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  };

  private handleDrop = (event: DragEvent): void => {
    const assetId = event.dataTransfer?.getData('application/x-mmo-visual-asset')?.trim()
      || this.draggedMapObjectAssetId
      || '';
    if (!assetId) {
      return;
    }
    const canvas = this.view;
    if (!(canvas instanceof HTMLCanvasElement)) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;
    const tile = this.renderer.screenToTile(screenX, screenY);
    const mapPosition = this.renderer.screenToMapPosition(event);
    if (!mapPosition || !tile) {
      return;
    }
    event.preventDefault();
    const transform = this.getMapObjectPlacementTransform();
    const tileMetrics = resolveTileDrawMetrics(this.grid, 1, tile.x, tile.y);
    this.events.emit('admin:mapObjectAttempt', {
      action: 'place',
      assetId,
      mapX: mapPosition.x,
      mapY: mapPosition.y,
      placementMode: 'tile-anchor',
      tileX: tile.x,
      tileY: tile.y,
      offsetX: ((mapPosition.x - tileMetrics.centerX) / tileMetrics.spanWidth) + transform.offsetX,
      offsetY: ((mapPosition.y - tileMetrics.centerY) / tileMetrics.spanHeight) + transform.offsetY,
      sizeMultiplier: transform.sizeMultiplier,
      flipHorizontal: transform.flipHorizontal,
      flipVertical: transform.flipVertical,
      rotationSteps: transform.rotationSteps,
      screenX: event.clientX,
      screenY: event.clientY,
    });
    this.draggedMapObjectAssetId = null;
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  };

  public setGrid(g: GridGeometry): void {
    this.grid = g;
  }

  public setInteractionMode(mode: 'movement' | 'placement'): void {
    this.interactionMode = mode;
    if (mode !== 'placement') {
      this.lastPlacementKey = null;
      this.biomeRegionDrag = null;
    }
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  }

  public setPlacementConfig(config: PlacementConfig | null): void {
    this.placementConfig = config;
    this.lastPlacementKey = null;
    if (config?.kind !== 'tile-biome-region') {
      this.biomeRegionDrag = null;
    }
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  }

  /** Handles keyboard input for movement/interaction. */
  public handleInput(event: KeyboardEvent): boolean {
    const normalizedKey = event.key?.length === 1 ? event.key.toLowerCase() : event.key;

    if (this.interactionMode === 'placement') {
      if (this.handlePlacementHotkey(normalizedKey, event)) {
        return true;
      }
    }

    // Q/E are reserved for camera orbit and should never enqueue gameplay intents.
    if (normalizedKey === 'q' || normalizedKey === 'e') {
      return false;
    }

    // A/D are turn-only controls across all grid shapes.
    if (
      normalizedKey === 'a' ||
      normalizedKey === 'd'
    ) {
      event.preventDefault();
      const direction = this.resolveTurnDirection(normalizedKey as 'a' | 'd');
      this.emitTurnIntent(direction);
      return true;
    }

    // Grid-specific turn mapping (triangle Q/E uses tile orientation).
    const turnReference = this.resolveReference();
    const gridTurnDirection = this.grid.resolveTurnFacingForKey(
      normalizedKey ?? '',
      turnReference.x,
      turnReference.y,
      this.getReferenceRotation()
    );
    if (typeof gridTurnDirection === 'number') {
      event.preventDefault();
      this.emitTurnIntent(gridTurnDirection);
      return true;
    }

    // Arrow keys are reserved for camera panning, not character movement/turning.
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return false;
    }

    // Handle interact key (G)
    if (event.key === 'g' || event.key === 'G') {
      event.preventDefault();
      this.emitInteractIntent();
      return true;
    }

    const reference = this.resolveReference();
    const moveTarget = this.resolveMovementTarget(normalizedKey ?? '', reference);
    if (!moveTarget) {
      return false;
    }

    event.preventDefault();
    this.emitMoveIntent(moveTarget);
    return true;
  }

  private resolveMovementTarget(key: string, reference: TilePosition): TilePosition | null {
    // Canonical forward action should move in the direction we are facing
    // at the end of the currently queued intents.
    if (key === 'w') {
      const facing = this.getReferenceRotation();
      const forward = this.resolveForwardDelta(reference, facing);
      if (forward) {
        return { x: reference.x + forward.dx, y: reference.y + forward.dy };
      }
    }
    if (key === 's') {
      const facing = this.getReferenceRotation();
      const backward = this.resolveForwardDelta(reference, facing + 180);
      if (backward) {
        return { x: reference.x + backward.dx, y: reference.y + backward.dy };
      }
    }

    const dir = this.resolveDirection(key);
    if (!dir) {
      return null;
    }
    return { x: reference.x + dir.dx, y: reference.y + dir.dy };
  }

  private resolveForwardDelta(
    reference: TilePosition,
    facing: number,
  ): { dx: number; dy: number } | null {
    const neighbors = this.grid.neighbors(reference.x, reference.y);
    if (neighbors.length === 0) {
      return null;
    }

    const snappedFacing = this.grid.snapFacing(facing);
    const facingIndex = this.grid.facingAngles.findIndex((angle) => angle === snappedFacing);
    if (facingIndex >= 0 && facingIndex < neighbors.length) {
      const neighbor = neighbors[facingIndex];
      return { dx: neighbor.x - reference.x, dy: neighbor.y - reference.y };
    }

    let best: { dx: number; dy: number } | null = null;
    let bestDiff = Number.POSITIVE_INFINITY;
    const normalizedFacing = ((snappedFacing % 360) + 360) % 360;

    for (const neighbor of neighbors) {
      const targetAngle = calculateTargetAngleFromTileCenters(this.grid, reference, neighbor);
      let diff = Math.abs((((targetAngle - normalizedFacing) % 360) + 360) % 360);
      if (diff > 180) {
        diff = 360 - diff;
      }
      if (diff < bestDiff) {
        bestDiff = diff;
        best = { dx: neighbor.x - reference.x, dy: neighbor.y - reference.y };
      }
    }

    return best;
  }

  private resolveDirection(key: string): { dx: number; dy: number } | undefined {
    if (this.grid.directions === 6) {
      const reference = this.resolveReference();
      const odd = (reference.x & 1) === 1;
      if (key === 'w') return { dx: 0, dy: -1 };
      if (key === 's') return { dx: 0, dy: 1 };
      if (key === 'e') return odd ? { dx: 1, dy: 0 } : { dx: 1, dy: -1 };
      if (key === 'q') return odd ? { dx: -1, dy: 0 } : { dx: -1, dy: -1 };
      return undefined;
    }

    const base = this.grid.keyDirections[key];
    if (!base) return undefined;
    return { dx: base.dx, dy: base.dy };
  }

  private emitInteractIntent(): void {
    this.events.emit(INPUT_INTENT_EVENT, { type: 'interact' });
  }

  private emitTurnIntent(direction: number): void {
    this.events.emit(INPUT_INTENT_EVENT, { type: 'turn', direction });
  }

  private resolveTurnDirection(key: 'a' | 'd'): number {
    const referenceFacing = this.getReferenceRotation();
    const turnDirection = key === 'a' ? 'left' : 'right';
    return this.grid.getNextEdgeFacing(referenceFacing, turnDirection);
  }

  private handleTileSelected = (event: Event): void => {
    const detail = (event as TileSelectedEvent).detail;

    if (this.interactionMode === 'placement') {
      if (!this.placementConfig) return;

      const effectivePlacement = this.resolvePlacementForTileSelection(detail);
      if (!effectivePlacement) {
        return;
      }
      const tilePosition = detail.tilePosition ?? detail.tile;
      const { x, y } = tilePosition;
      const placementTransformKey =
        effectivePlacement.kind === 'map-object-place' || effectivePlacement.kind === 'path-tile-place'
          ? [
            this.mapObjectPlacementTransform.offsetX,
            this.mapObjectPlacementTransform.offsetY,
            this.mapObjectPlacementTransform.sizeMultiplier,
            this.mapObjectPlacementTransform.flipHorizontal ? 1 : 0,
            this.mapObjectPlacementTransform.flipVertical ? 1 : 0,
            this.mapObjectPlacementTransform.rotationSteps,
          ].join(':')
          : '';
      const placementKey = `${effectivePlacement.kind}:${x}:${y}:${detail.button ?? -1}:${detail.buttons ?? 0}:${placementTransformKey}`;
      if (placementKey === this.lastPlacementKey) {
        return;
      }
      this.lastPlacementKey = placementKey;

      if (effectivePlacement.kind === 'npc-place' || effectivePlacement.kind === 'npc-remove') {
        this.events.emit('admin:npcAttempt', {
          action: effectivePlacement.kind === 'npc-remove' ? 'remove' : 'place',
          npcType: effectivePlacement.kind === 'npc-place' ? effectivePlacement.npcType : undefined,
          x,
          y
        });
        return;
      }

      if (effectivePlacement.kind === 'portal-place' || effectivePlacement.kind === 'portal-remove') {
        this.events.emit('admin:portalAttempt', {
          action: effectivePlacement.kind === 'portal-remove' ? 'remove' : 'place',
          x,
          y,
          destMapId: effectivePlacement.kind === 'portal-place' ? effectivePlacement.destMapId : undefined,
          destX: effectivePlacement.kind === 'portal-place' ? effectivePlacement.destX : undefined,
          destY: effectivePlacement.kind === 'portal-place' ? effectivePlacement.destY : undefined,
          autoPortal: effectivePlacement.kind === 'portal-place' ? effectivePlacement.autoPortal === true : undefined,
        });
        return;
      }

      if (effectivePlacement.kind === 'map-object-place' || effectivePlacement.kind === 'map-object-remove') {
        if (!detail.mapPosition) {
          return;
        }
        if ((detail.buttons ?? 0) !== 0) {
          return;
        }
        this.events.emit('admin:mapObjectAttempt', {
          action: effectivePlacement.kind === 'map-object-remove' ? 'remove' : 'place',
          assetId: effectivePlacement.kind === 'map-object-place' ? effectivePlacement.assetId : undefined,
          mapX: detail.mapPosition.x,
          mapY: detail.mapPosition.y,
          placementMode: effectivePlacement.kind === 'map-object-place' ? 'tile-anchor' : undefined,
          tileX: effectivePlacement.kind === 'map-object-place' ? x : undefined,
          tileY: effectivePlacement.kind === 'map-object-place' ? y : undefined,
          offsetX: effectivePlacement.kind === 'map-object-place'
            ? this.mapObjectPlacementTransform.offsetX
            : 0,
          offsetY: effectivePlacement.kind === 'map-object-place'
            ? this.mapObjectPlacementTransform.offsetY
            : 0,
          sizeMultiplier: effectivePlacement.kind === 'map-object-place'
            ? this.mapObjectPlacementTransform.sizeMultiplier
            : undefined,
          flipHorizontal: effectivePlacement.kind === 'map-object-place'
            ? this.mapObjectPlacementTransform.flipHorizontal
            : undefined,
          flipVertical: effectivePlacement.kind === 'map-object-place'
            ? this.mapObjectPlacementTransform.flipVertical
            : undefined,
          rotationSteps: effectivePlacement.kind === 'map-object-place'
            ? this.mapObjectPlacementTransform.rotationSteps
            : undefined,
          screenX: detail.screenPosition?.x,
          screenY: detail.screenPosition?.y,
        });
        return;
      }

      if (
        effectivePlacement.kind === 'path-tile-autotile'
        || effectivePlacement.kind === 'path-tile-place'
        || effectivePlacement.kind === 'path-tile-erase'
      ) {
        this.events.emit('admin:pathTileAttempt', {
          atlasId: effectivePlacement.atlasId,
          tileIndex: effectivePlacement.kind === 'path-tile-place' ? effectivePlacement.tileIndex : null,
          autotile: effectivePlacement.kind === 'path-tile-autotile',
          flipHorizontal: effectivePlacement.kind === 'path-tile-place' ? this.mapObjectPlacementTransform.flipHorizontal : undefined,
          flipVertical: effectivePlacement.kind === 'path-tile-place' ? this.mapObjectPlacementTransform.flipVertical : undefined,
          rotationSteps: effectivePlacement.kind === 'path-tile-place' ? this.mapObjectPlacementTransform.rotationSteps : undefined,
          x,
          y,
        });
        return;
      }
      if (effectivePlacement.kind === 'tile-biome-region') {
        const isPressed = ((detail.buttons ?? 0) & 0b01) !== 0;
        const nextTile = { x, y };
        if (!isPressed) {
          return;
        }
        if (!this.biomeRegionDrag || this.biomeRegionDrag.biome !== effectivePlacement.biome) {
          this.biomeRegionDrag = {
            biome: effectivePlacement.biome,
            start: nextTile,
            current: nextTile,
          };
        } else {
          this.biomeRegionDrag = {
            ...this.biomeRegionDrag,
            current: nextTile,
          };
        }
        this.emitBiomeRegionPlacementPreviewChanged();
        this.renderer.requestRender();
        return;
      }
      this.events.emit('admin:mapTileAttempt', {
        layer:
          effectivePlacement.kind === 'tile-terrain' || effectivePlacement.kind === 'tile-terrain-erase'
            ? 'terrain'
            : 'biome',
        value:
          effectivePlacement.kind === 'tile-terrain'
            ? effectivePlacement.terrain
            : effectivePlacement.kind === 'tile-terrain-erase'
              ? '__erase__'
              : effectivePlacement.kind === 'tile-biome'
                ? effectivePlacement.biome
                : '__erase__',
        x,
        y
      });
      return;
    }

    if (detail.shiftKey) {
      return;
    }

    if (detail.button === 2) {
      const reference = this.resolveReference();
      const target = detail.tile;

      // Ignore if clicked tile is current tile
      if (reference.x === target.x && reference.y === target.y) {
        return;
      }

      const angle = calculateTargetAngleFromTileCenters(this.grid, reference, target);
      const snappedDirection = this.grid.snapFacing(angle);
      this.emitTurnIntent(snappedDirection);
      return;
    }

    if (((detail.buttons ?? 0) & 0b10) !== 0) {
      return;
    }

    this.emitMoveIntent(detail.tile);
  };

  private handleDirectionClick = (event: Event): void => {
    const button = event.currentTarget as HTMLButtonElement | undefined;
    if (!button) {
      return;
    }
    const directionKey = button.dataset.direction;
    if (!directionKey) {
      return;
    }

    const delta = buttonDirectionMap[directionKey];
    if (!delta) {
      return;
    }

    const reference = this.resolveReference();
    this.emitMoveIntent({ x: reference.x + delta.x, y: reference.y + delta.y });
  };

  private resolveReference(): TilePosition {
    return this.getReferencePosition();
  }

  private emitMoveIntent(position: TilePosition): void {
    this.events.emit(INPUT_INTENT_EVENT, { type: 'moveToTile', position });
  }

  public requestClearQueue(): void {
    this.events.emit(INPUT_CLEAR_EVENT);
  }

  private readonly resetPlacementDragState = (): void => {
    this.lastPlacementKey = null;
    this.commitBiomeRegionDrag();
  };

  private handlePlacementHotkey(key: string, event: KeyboardEvent): boolean {
    if (
      (
        this.placementConfig?.kind !== 'map-object-place'
        && this.placementConfig?.kind !== 'path-tile-place'
        && this.placementConfig?.kind !== 'path-tile-autotile'
      )
      || event.repeat
    ) {
      return false;
    }

    if (this.placementConfig?.kind === 'map-object-place' && event.shiftKey && key === 'ArrowLeft') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        offsetX: Math.max(-1, this.mapObjectPlacementTransform.offsetX - MAP_OBJECT_OFFSET_NUDGE_STEP),
      };
    } else if (this.placementConfig?.kind === 'map-object-place' && event.shiftKey && key === 'ArrowRight') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        offsetX: Math.min(1, this.mapObjectPlacementTransform.offsetX + MAP_OBJECT_OFFSET_NUDGE_STEP),
      };
    } else if (this.placementConfig?.kind === 'map-object-place' && event.shiftKey && key === 'ArrowUp') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        offsetY: Math.max(-1, this.mapObjectPlacementTransform.offsetY - MAP_OBJECT_OFFSET_NUDGE_STEP),
      };
    } else if (this.placementConfig?.kind === 'map-object-place' && event.shiftKey && key === 'ArrowDown') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        offsetY: Math.min(1, this.mapObjectPlacementTransform.offsetY + MAP_OBJECT_OFFSET_NUDGE_STEP),
      };
    } else if (key === 'Insert') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        rotationSteps: (this.mapObjectPlacementTransform.rotationSteps + 11) % 12,
      };
    } else if (key === 'Home') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        flipVertical: !this.mapObjectPlacementTransform.flipVertical,
      };
    } else if (key === 'Delete') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        offsetX: 0,
        offsetY: 0,
        flipHorizontal: false,
        flipVertical: false,
        rotationSteps: 0,
      };
    } else if (key === 'End') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        flipHorizontal: !this.mapObjectPlacementTransform.flipHorizontal,
      };
    } else if (key === 'PageUp') {
      this.mapObjectPlacementTransform = {
        ...this.mapObjectPlacementTransform,
        rotationSteps: (this.mapObjectPlacementTransform.rotationSteps + 1) % 12,
      };
    } else {
      return false;
    }

    event.preventDefault();
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
    return true;
  }

  private getMapObjectPlacementTransform(): MapObjectPlacementTransform {
    return { ...this.mapObjectPlacementTransform };
  }

  private emitMapObjectPlacementPreviewChanged(detail?: MapObjectPlacementPreviewDetail): void {
    const nextDetail = detail ?? (
      this.interactionMode === 'placement' && this.placementConfig?.kind === 'map-object-place'
        ? {
          active: true,
          assetId: this.placementConfig.assetId,
          previewMode: 'tile-anchor',
          tileX: Number.NaN,
          tileY: Number.NaN,
          offsetX: this.mapObjectPlacementTransform.offsetX,
          offsetY: this.mapObjectPlacementTransform.offsetY,
          ...this.getMapObjectPlacementTransform(),
        }
        : {
          active: false,
          assetId: null,
          previewMode: 'tile-anchor',
          ...this.getMapObjectPlacementTransform(),
        }
    );

    this.renderer.dispatchEvent(new CustomEvent('mapObjectPlacementPreviewChanged', { detail: nextDetail }));
    this.emitPathTilePlacementPreviewChanged();
    this.emitBiomeRegionPlacementPreviewChanged();
  }

  private emitPathTilePlacementPreviewChanged(): void {
    const detail: PathTilePlacementPreviewDetail =
      this.interactionMode === 'placement' && this.placementConfig?.kind === 'path-tile-place'
        ? {
          active: true,
          atlasId: this.placementConfig.atlasId,
          tileIndex: this.placementConfig.tileIndex,
          autotile: false,
          ...this.getMapObjectPlacementTransform(),
        }
        : this.interactionMode === 'placement' && this.placementConfig?.kind === 'path-tile-autotile'
          ? {
            active: true,
            atlasId: this.placementConfig.atlasId,
            tileIndex: 0,
            autotile: true,
            ...this.getMapObjectPlacementTransform(),
          }
          : {
            active: false,
            atlasId: null,
            tileIndex: null,
            autotile: false,
          };
    this.renderer.dispatchEvent(new CustomEvent('pathTilePlacementPreviewChanged', { detail }));
  }

  private emitBiomeRegionPlacementPreviewChanged(): void {
    const detail: BiomeRegionPlacementPreviewDetail =
      this.interactionMode === 'placement' && this.biomeRegionDrag
        ? {
          active: true,
          biome: this.biomeRegionDrag.biome,
          startX: this.biomeRegionDrag.start.x,
          startY: this.biomeRegionDrag.start.y,
          endX: this.biomeRegionDrag.current.x,
          endY: this.biomeRegionDrag.current.y,
        }
        : {
          active: false,
          biome: null,
        };
    this.renderer.dispatchEvent(new CustomEvent('biomeRegionPlacementPreviewChanged', { detail }));
  }

  private commitBiomeRegionDrag(): void {
    if (!this.biomeRegionDrag) {
      this.emitBiomeRegionPlacementPreviewChanged();
      return;
    }
    const { biome, start, current } = this.biomeRegionDrag;
    this.biomeRegionDrag = null;
    this.emitBiomeRegionPlacementPreviewChanged();
    this.renderer.requestRender();
    this.events.emit<MapBiomeRegionAttemptEvent>('admin:mapBiomeRegionAttempt', {
      biome,
      startX: start.x,
      startY: start.y,
      endX: current.x,
      endY: current.y,
    });
  }

  private handleMapObjectPlacementScaleChanged = (): void => {
    this.mapObjectPlacementTransform = {
      ...this.mapObjectPlacementTransform,
      sizeMultiplier: getVisualAssetSizeMultiplier(),
    };
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  };

  private handleWindowDragEnd = (): void => {
    if (!this.draggedMapObjectAssetId) {
      return;
    }
    this.draggedMapObjectAssetId = null;
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  };

  private handleMapObjectDragStarted = (event: Event): void => {
    const detail = (event as CustomEvent<{ assetId?: string | null }>).detail;
    const assetId = detail?.assetId?.trim();
    if (!assetId) {
      return;
    }
    this.draggedMapObjectAssetId = assetId;
  };

  private handleMapObjectDragEnded = (): void => {
    this.draggedMapObjectAssetId = null;
    this.emitMapObjectPlacementPreviewChanged();
    this.renderer.requestRender();
  };

  private resolvePlacementForTileSelection(detail: TileSelectedEvent['detail']): PlacementConfig | null {
    if (!this.placementConfig) {
      return null;
    }
    const isRightButton = detail.button === 2 || ((detail.buttons ?? 0) & 0b10) !== 0;
    if (!isRightButton) {
      return this.placementConfig;
    }
    switch (this.placementConfig.kind) {
      case 'tile-terrain':
      case 'tile-terrain-erase':
        return { kind: 'tile-terrain-erase' };
      case 'tile-biome':
      case 'tile-biome-erase':
      case 'tile-biome-region':
        return { kind: 'tile-biome-erase' };
      case 'path-tile-autotile':
      case 'path-tile-place':
      case 'path-tile-erase':
        return { kind: 'path-tile-erase', atlasId: this.placementConfig.atlasId };
      case 'npc-place':
      case 'npc-remove':
        return { kind: 'npc-remove' };
      case 'map-object-place':
      case 'map-object-remove':
        return { kind: 'map-object-remove' };
      case 'portal-place':
      case 'portal-remove':
        return { kind: 'portal-remove' };
    }
  }
}
